import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as settingsApi from '@/api/settings.api';
import type { ConversationWithRelations } from '@/lib/types';
import { agentStatusFrom } from '@/lib/agent-status';
import { useAgentTurnsStore } from '@/store/agent-turns.store';
import { cn } from '@/lib/cn';

/**
 * <AgentStatusDot> — estado REAL del agente AI en la conversación:
 *  · gris "Sin agente" / "Bot en pausa" / "Fuera de horario"
 *  · verde "Disponible"
 *  · azul palpitante "Respondiendo…" (evento agent.turn del backend)
 * Nunca inventa: el horario es la MISMA lógica del motor (shared-types) y el
 * "respondiendo" viene del pipeline. Auto-apagado a los 90 s si el `finished`
 * se perdió (reconnect del socket): mejor apagado que mentiroso.
 */

/** Un turno "en vuelo" hace más de esto se considera estado pegado. */
const RESPONDING_STALE_MS = 90_000;

const DOT_TONE = {
  gray: 'bg-slate-400',
  green: 'bg-emerald-500',
  blue: 'bg-blue-500 animate-pulse',
} as const;

export function AgentStatusDot({ conversation }: { conversation: ConversationWithRelations }) {
  const { data: schedule } = useQuery({
    queryKey: ['settings', 'agent-schedule'],
    queryFn: settingsApi.getAgentSchedule,
    staleTime: 60_000,
  });
  const respondingSince = useAgentTurnsStore((s) => s.respondingSince[conversation.id]);

  // Tic de 1s SOLO mientras hay turno en vuelo: expira el estado pegado y
  // re-evalúa el borde de horario sin polling permanente.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!respondingSince) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [respondingSince]);

  const responding =
    respondingSince != null && Date.now() - respondingSince < RESPONDING_STALE_MS;
  const hasAgent = Boolean(
    (conversation.agent && conversation.agent.status === 'ACTIVE') ||
      conversation.channel?.defaultAgentId,
  );
  const status = agentStatusFrom({
    conversationStatus: conversation.status,
    hasAgent,
    schedule,
    responding,
  });

  return (
    <span
      className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400"
      title={`Agente AI: ${status.label}`}
    >
      <span className={cn('h-2 w-2 rounded-full', DOT_TONE[status.tone])} />
      {status.label}
    </span>
  );
}
